/**
 * Zod Validation Schemas for Parent Registration (Tuition Case Posting)
 */

import { z } from 'zod';

/**
 * Step 0: Tuition Details
 */
export const tuitionDetailsSchema = z.object({
  tuitionType: z.enum(['physical', 'online', 'both'], {
    message: 'Please select a tuition type',
  }),
  educationSystem: z.string().min(1, 'Please select an education system'),
  level: z.string().min(1, 'Please select an education level'),
});

/**
 * Step 1: Subject & Duration
 */
export const subjectDurationSchema = z.object({
  subject: z.string().min(1, 'Please select a subject'),
  teachingLanguages: z
    .array(z.string())
    .min(1, 'Please select at least one teaching language'),
  lessonsPerWeek: z
    .number({
      message: 'Lessons per week must be a number',
    })
    .int('Lessons per week must be an integer')
    .min(1, 'At least one lesson per week is required')
    .max(5, 'Maximum 5 lessons per week'),
  lessonDuration: z
    .number({
      message: 'Lesson duration must be a number',
    })
    .min(0.5, 'Minimum lesson duration is 0.5 hours')
    .max(8, 'Maximum lesson duration is 8 hours'),
  tutorRemarks: z.string().max(500, 'Remarks must be less than 500 characters').optional(),
});

/**
 * Step 2: Location & Preferred Time
 * Note: These fields are required for physical/both tuition, optional for online.
 */
export const locationTimeSchema = z.object({
  state: z.string().optional(),
  district: z.string().optional(),
  areaNote: z.string().max(100, 'Area note must be less than 100 characters').optional(),
  preferredTimes: z
    .record(z.string(), z.array(z.string()))
    .refine(
      (times) =>
        Object.keys(times).length > 0 &&
        Object.values(times).some((slots) => slots.length > 0),
      { message: 'Please select at least one preferred time slot' }
    ),
});

const addInPersonLocationIssues = (
  data: { tuitionType: string; state?: string; district?: string },
  ctx: z.RefinementCtx
) => {
  if (data.tuitionType === 'online') return;

  if (!data.state || data.state.trim() === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['state'],
      message: 'Please select a state',
    });
  }

  if (!data.district || data.district.trim() === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['district'],
      message: 'Please select a district',
    });
  }
};

const locationTimeStepSchema = locationTimeSchema
  .extend({
    tuitionType: z.string(),
  })
  .superRefine((data, ctx) => {
    addInPersonLocationIssues(data, ctx);
  });

/**
 * Step 3: Budget
 */
export const budgetSchema = z.object({
  hourlyRate: z
    .number({
      message: 'Hourly rate must be a number',
    })
    .int('Hourly rate must be a whole number')
    .min(10, 'Minimum hourly rate is RM 10')
    .max(800, 'Maximum hourly rate is RM 800'),
  termsAccepted: z
    .boolean()
    .refine((val) => val === true, {
      message: 'You must accept the terms and conditions',
    }),
});

/**
 * Complete parent registration form schema (all steps combined)
 * For online tuition, location fields are not required.
 * For physical/both tuition, state and district are required.
 */
export const parentRegistrationSchema = tuitionDetailsSchema
  .merge(subjectDurationSchema)
  .merge(locationTimeSchema)
  .merge(budgetSchema)
  .superRefine((data, ctx) => {
    addInPersonLocationIssues(data, ctx);
  });

export type TuitionDetailsData = z.infer<typeof tuitionDetailsSchema>;
export type SubjectDurationData = z.infer<typeof subjectDurationSchema>;
export type LocationTimeData = z.infer<typeof locationTimeSchema>;
export type BudgetData = z.infer<typeof budgetSchema>;
export type ParentRegistrationData = z.infer<typeof parentRegistrationSchema>;

/**
 * Per-step validation helper — shared between ParentRegistrationModal and EditCasePage.
 * Centralises which schema runs at each step, including the online-tuition skip for step 2.
 * Null values from the API are coerced to safe defaults so callers don't need to sanitise first.
 */
type ParentStepInput = {
  tuitionType: string;
  educationSystem: string;
  level: string;
  subject: string;
  teachingLanguages: string[];
  lessonsPerWeek: number;
  lessonDuration: number;
  tutorRemarks?: string | null;
  state?: string | null;
  district?: string | null;
  areaNote?: string | null;
  preferredTimes?: Record<string, string[]>;
  hourlyRate: number;
  termsAccepted?: boolean | null;
};

export const parseParentStep = (step: number, data: ParentStepInput) => {
  if (step === 0) {
    return tuitionDetailsSchema.safeParse({
      tuitionType: data.tuitionType,
      educationSystem: data.educationSystem,
      level: data.level,
    });
  }
  if (step === 1) {
    return subjectDurationSchema.safeParse({
      subject: data.subject,
      teachingLanguages: data.teachingLanguages,
      lessonsPerWeek: data.lessonsPerWeek,
      lessonDuration: data.lessonDuration,
      tutorRemarks: data.tutorRemarks ?? '',
    });
  }
  if (step === 2) {
    return locationTimeStepSchema.safeParse({
      tuitionType: data.tuitionType,
      state: data.state ?? '',
      district: data.district ?? '',
      areaNote: data.areaNote ?? '',
      preferredTimes: data.preferredTimes ?? {},
    });
  }
  return budgetSchema.safeParse({
    hourlyRate: data.hourlyRate,
    termsAccepted: data.termsAccepted ?? false,
  });
};
